import { ref } from 'vue';
import type { OperationsConfiguration } from '@/types/OperationsConfiguration';
import type { EditedBandItem } from '@/types/GridTypes';
import {
    createPayload, fetchConfiguration as fetchConfig, updateConfiguration as updateConfig, resetConfiguration as resetConfig
} from '@/utils/operationsConfigurationUtils';

export function useBacklogInsightsConfiguration() {
    const BASE_URL = '/workactivity/back-office/backlog/configurations';

    const configurationData = ref<OperationsConfiguration>({
        globalConfig: {
            ageBandConfig: {
                items: [],
                defaultAgeBand: ''
            }
        }
    });
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const fetchConfiguration = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            const data = await fetchConfig(BASE_URL);
            configurationData.value = data;
            return data;
        } catch (err) {
            error.value = 'Failed to fetch configuration';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const updateConfiguration = async (editedBands?: EditedBandItem[]) => {
        isLoading.value = true;
        error.value = null;
        try {
            const payload = createPayload(editedBands, configurationData.value);
            const hasId = configurationData.value.globalConfig.ageBandConfig._id;
            const data = await updateConfig(BASE_URL, payload, hasId);
            configurationData.value = data;
            return data;
        } catch (err) {
            error.value = 'Failed to update configuration';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    const resetConfiguration = async () => {
        isLoading.value = true;
        error.value = null;
        try {
            const configId = configurationData.value.globalConfig.ageBandConfig._id!;
            const data = await resetConfig(BASE_URL, configId);
            configurationData.value = data;
            return data;
        } catch (err) {
            error.value = 'Failed to reset configuration';
            throw err;
        } finally {
            isLoading.value = false;
        }
    };

    return {
        configurationData,
        isLoading,
        error,
        fetchConfiguration,
        updateConfiguration,
        resetConfiguration
    };
}